/**
 * VÉRAULT Travels — Master Client Profile
 * The CANONICAL client detail view used everywhere:
 *   - Dashboard > Manage > Clients > [client]
 *   - Trip Profile > View Master Profile
 * 
 * Sections: Contact, Address, Passport/Travel, Loyalty, Emergency, Preferences, Notes
 * Shows: Linked trips, Associated travelers (clickable)
 * Supports: Full CRUD editing
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Edit2, Save, X, Plus, Trash2, User, Mail, Phone,
  MapPin, Plane, CreditCard, Shield, Heart, AlertCircle, FileText,
  ChevronRight, Calendar, Users, Loader2, Globe, Bookmark, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { formatDate } from '../utils/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================
// SECTION COMPONENT — collapsible group
// ============================================================
function Section({ title, icon: Icon, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[rgba(0,0,0,0.06)] rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 bg-[#F9F8F4] hover:bg-[#F5F3ED] transition-colors text-left"
      >
        <Icon className="w-4 h-4 text-[#C8A85A]" />
        <span className="text-sm font-medium text-[#1A1A1A] flex-1">{title}</span>
        <ChevronRight className={`w-4 h-4 text-[#8C8C8C] transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && <div className="p-4 space-y-3">{children}</div>}
    </div>
  );
}

// ============================================================
// FIELD — display or edit
// ============================================================
function Field({ label, value, field, editing, form, onChange, type = 'text', placeholder = '' }) {
  if (editing) {
    return (
      <div>
        <Label className="text-[#8C8C8C] text-xs">{label}</Label>
        <Input
          type={type}
          value={form[field] ?? ''}
          onChange={e => onChange(field, e.target.value)}
          placeholder={placeholder || label}
          className="text-sm h-8"
          data-testid={`field-${field}`}
        />
      </div>
    );
  }
  return (
    <div>
      <p className="text-[10px] text-[#8C8C8C] uppercase tracking-wider">{label}</p>
      <p className="text-sm text-[#1A1A1A]">{value || '—'}</p>
    </div>
  );
}

// ============================================================
// LIST FIELD — repeating items (loyalty, credit cards, dates, ff)
// ============================================================
function ListField({ label, items = [], editing, fields, onAdd, onRemove, onChange }) {
  if (!editing && (!items || items.length === 0)) {
    return (
      <div>
        <p className="text-[10px] text-[#8C8C8C] uppercase tracking-wider">{label}</p>
        <p className="text-sm text-[#8C8C8C] italic">None</p>
      </div>
    );
  }
  return (
    <div>
      <p className="text-[10px] text-[#8C8C8C] uppercase tracking-wider mb-1">{label}</p>
      {(items || []).map((item, idx) => (
        <div key={idx} className="flex items-center gap-2 mb-1">
          {fields.map(f => (
            editing ? (
              <Input
                key={f.key}
                value={item[f.key] || ''}
                onChange={e => onChange(idx, f.key, e.target.value)}
                placeholder={f.label}
                className="text-xs h-7 flex-1"
              />
            ) : (
              <span key={f.key} className="text-sm text-[#1A1A1A]">{item[f.key]}{f.suffix || ''}</span>
            )
          ))}
          {editing && (
            <button onClick={() => onRemove(idx)} className="p-1 hover:bg-red-50 rounded"><Trash2 className="w-3 h-3 text-red-400" /></button>
          )}
        </div>
      ))}
      {editing && (
        <Button size="sm" variant="ghost" onClick={onAdd} className="text-xs h-6 mt-1">
          <Plus className="w-3 h-3 mr-1" />Add
        </Button>
      )}
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================
export function MasterClientProfile({ clientId, onBack }) {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const fetchClient = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}`);
      setClient(res.data);
      setForm(res.data);
    } catch (e) {
      toast.error('Failed to load client');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (clientId) fetchClient();
  }, [clientId, fetchClient]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleListChange = (listField, idx, key, value) => {
    setForm(prev => {
      const list = [...(prev[listField] || [])];
      list[idx] = { ...list[idx], [key]: value };
      return { ...prev, [listField]: list };
    });
  };
  const handleListAdd = (listField, template) => {
    setForm(prev => ({ ...prev, [listField]: [...(prev[listField] || []), template] }));
  };
  const handleListRemove = (listField, idx) => {
    setForm(prev => ({ ...prev, [listField]: (prev[listField] || []).filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { trips, travelers, financials, created_at, id, ...updateData } = form;
      await axios.patch(`${API}/clients/${clientId}`, updateData);
      toast.success('Client saved');
      setEditing(false);
      fetchClient();
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this client permanently?')) return;
    try {
      await axios.delete(`${API}/clients/${clientId}`);
      toast.success('Client deleted');
      onBack?.();
    } catch {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#C8A85A]" /></div>;
  }
  if (!client) {
    return <div className="text-center py-16 text-sm text-[#8C8C8C]">Client not found</div>;
  }

  const trips = client.trips || [];
  const travelers = client.travelers || [];
  const fin = client.financials || {};

  return (
    <div className="space-y-5" data-testid="master-client-profile">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack} className="p-1">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          )}
          <div>
            <h2 className="font-serif text-xl text-[#1A1A1A]" data-testid="client-name">{client.full_name}</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${client.status === 'active' ? 'bg-emerald-50 text-emerald-700' : client.status === 'lead' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                {client.status || 'lead'}
              </span>
              {client.email && <span className="text-xs text-[#8C8C8C]">{client.email}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setForm(client); }} className="text-xs"><X className="w-3 h-3 mr-1" />Cancel</Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className="text-xs bg-[#C8A85A] hover:bg-[#B39049]" data-testid="save-client-btn">
                {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}Save
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="text-xs" data-testid="edit-client-btn"><Edit2 className="w-3 h-3 mr-1" />Edit</Button>
              <Button size="sm" variant="ghost" onClick={handleDelete} className="text-xs text-red-500 hover:bg-red-50"><Trash2 className="w-3 h-3" /></Button>
            </>
          )}
        </div>
      </div>

      {/* Financial Summary Bar */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total Billed', value: `$${(fin.total_billed || 0).toLocaleString()}`, color: 'text-[#1A1A1A]' },
          { label: 'Total Paid', value: `$${(fin.total_paid || 0).toLocaleString()}`, color: 'text-emerald-600' },
          { label: 'Outstanding', value: `$${(fin.total_outstanding || 0).toLocaleString()}`, color: fin.total_outstanding > 0 ? 'text-amber-600' : 'text-[#8C8C8C]' },
          { label: 'Trips', value: trips.length, color: 'text-[#C8A85A]' },
        ].map((s, i) => (
          <div key={i} className="bg-[#F9F8F4] rounded-lg p-3 text-center">
            <p className="text-[10px] text-[#8C8C8C] uppercase tracking-wider">{s.label}</p>
            <p className={`text-lg font-semibold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Sections */}
      <Section title="Contact Information" icon={User}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Full Name" value={client.full_name} field="full_name" editing={editing} form={form} onChange={handleChange} />
          <Field label="Email" value={client.email} field="email" editing={editing} form={form} onChange={handleChange} type="email" />
          <Field label="Phone" value={client.phone} field="phone" editing={editing} form={form} onChange={handleChange} />
          <Field label="Status" value={client.status} field="status" editing={editing} form={form} onChange={handleChange} />
        </div>
      </Section>

      <Section title="Address" icon={MapPin} defaultOpen={false}>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Field label="Street Address" value={client.street_address || client.address} field="street_address" editing={editing} form={form} onChange={handleChange} /></div>
          <Field label="City" value={client.city} field="city" editing={editing} form={form} onChange={handleChange} />
          <Field label="State / Province" value={client.state_province} field="state_province" editing={editing} form={form} onChange={handleChange} />
          <Field label="Postal Code" value={client.postal_code} field="postal_code" editing={editing} form={form} onChange={handleChange} />
          <Field label="Country" value={client.country} field="country" editing={editing} form={form} onChange={handleChange} />
        </div>
      </Section>

      <Section title="Important Dates" icon={Calendar} defaultOpen={false}>
        <Field label="Celebration Dates" value={client.celebration_dates} field="celebration_dates" editing={editing} form={form} onChange={handleChange} />
        <ListField
          label="Special Dates"
          items={form.important_dates || []}
          editing={editing}
          fields={[{ key: 'label', label: 'Label' }, { key: 'date', label: 'Date' }]}
          onAdd={() => handleListAdd('important_dates', { label: '', date: '' })}
          onRemove={(idx) => handleListRemove('important_dates', idx)}
          onChange={(idx, key, val) => handleListChange('important_dates', idx, key, val)}
        />
      </Section>

      <Section title="Dietary & Allergies" icon={AlertCircle} defaultOpen={false}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Dietary Needs" value={client.dietary_needs} field="dietary_needs" editing={editing} form={form} onChange={handleChange} />
          <Field label="Allergies" value={client.allergies} field="allergies" editing={editing} form={form} onChange={handleChange} />
        </div>
      </Section>

      <Section title="Passport & Travel Documents" icon={Globe} defaultOpen={false}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Passport Number" value={client.passport_number} field="passport_number" editing={editing} form={form} onChange={handleChange} />
          <Field label="Issuing Country" value={client.passport_issuing_country} field="passport_issuing_country" editing={editing} form={form} onChange={handleChange} />
          <Field label="Expiration Date" value={client.passport_expiration_date} field="passport_expiration_date" editing={editing} form={form} onChange={handleChange} type="date" />
          <Field label="Nationality" value={client.nationality} field="nationality" editing={editing} form={form} onChange={handleChange} />
          <Field label="Known Traveler Number" value={client.known_traveler_number} field="known_traveler_number" editing={editing} form={form} onChange={handleChange} />
          <Field label="Redress Number" value={client.redress_number} field="redress_number" editing={editing} form={form} onChange={handleChange} />
        </div>
      </Section>

      <Section title="Flight Preferences" icon={Plane} defaultOpen={false}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Home Airport" value={client.home_airport} field="home_airport" editing={editing} form={form} onChange={handleChange} placeholder="e.g. JFK" />
          <Field label="Airline Preferences" value={client.airline_preferences} field="airline_preferences" editing={editing} form={form} onChange={handleChange} />
          <Field label="Seat Preference" value={client.seat_preference} field="seat_preference" editing={editing} form={form} onChange={handleChange} placeholder="Window, Aisle, etc." />
        </div>
        <ListField
          label="Frequent Flyer Programs"
          items={form.frequent_flyer_programs || []}
          editing={editing}
          fields={[{ key: 'airline', label: 'Airline' }, { key: 'number', label: 'Number' }]}
          onAdd={() => handleListAdd('frequent_flyer_programs', { airline: '', number: '' })}
          onRemove={(idx) => handleListRemove('frequent_flyer_programs', idx)}
          onChange={(idx, key, val) => handleListChange('frequent_flyer_programs', idx, key, val)}
        />
      </Section>

      <Section title="Loyalty Programs" icon={Bookmark} defaultOpen={false}>
        <ListField
          label="Programs"
          items={form.loyalty_programs || []}
          editing={editing}
          fields={[{ key: 'program', label: 'Program' }, { key: 'number', label: 'Number' }]}
          onAdd={() => handleListAdd('loyalty_programs', { program: '', number: '' })}
          onRemove={(idx) => handleListRemove('loyalty_programs', idx)}
          onChange={(idx, key, val) => handleListChange('loyalty_programs', idx, key, val)}
        />
      </Section>

      <Section title="Credit Cards" icon={CreditCard} defaultOpen={false}>
        <ListField
          label="Cards"
          items={form.credit_cards || []}
          editing={editing}
          fields={[{ key: 'name', label: 'Card Name' }, { key: 'last_four', label: 'Last 4' }, { key: 'type', label: 'Type' }]}
          onAdd={() => handleListAdd('credit_cards', { name: '', last_four: '', type: '' })}
          onRemove={(idx) => handleListRemove('credit_cards', idx)}
          onChange={(idx, key, val) => handleListChange('credit_cards', idx, key, val)}
        />
      </Section>

      <Section title="Emergency Contact" icon={Shield} defaultOpen={false}>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Name" value={client.emergency_contact_name} field="emergency_contact_name" editing={editing} form={form} onChange={handleChange} />
          <Field label="Phone" value={client.emergency_contact_phone} field="emergency_contact_phone" editing={editing} form={form} onChange={handleChange} />
          <Field label="Relationship" value={client.emergency_contact_relationship} field="emergency_contact_relationship" editing={editing} form={form} onChange={handleChange} />
        </div>
      </Section>

      <Section title="Travel Preferences" icon={Heart} defaultOpen={false}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Travel Pace" value={client.travel_pace} field="travel_pace" editing={editing} form={form} onChange={handleChange} />
          <Field label="Budget Range" value={client.budget_range} field="budget_range" editing={editing} form={form} onChange={handleChange} />
        </div>
        {editing ? (
          <div>
            <Label className="text-[#8C8C8C] text-xs">Travel Preferences</Label>
            <Textarea value={form.travel_preferences || ''} onChange={e => handleChange('travel_preferences', e.target.value)} className="text-sm min-h-[60px]" />
          </div>
        ) : client.travel_preferences ? (
          <div><p className="text-[10px] text-[#8C8C8C] uppercase tracking-wider">Travel Preferences</p><p className="text-sm text-[#1A1A1A] whitespace-pre-wrap">{client.travel_preferences}</p></div>
        ) : null}
      </Section>

      <Section title="Notes" icon={FileText} defaultOpen={false}>
        {editing ? (
          <Textarea value={form.notes || ''} onChange={e => handleChange('notes', e.target.value)} placeholder="Internal notes..." className="text-sm min-h-[80px]" />
        ) : (
          <p className="text-sm text-[#595959] whitespace-pre-wrap">{client.notes || 'No notes'}</p>
        )}
      </Section>

      {/* ASSOCIATED TRAVELERS */}
      <div className="border border-[rgba(0,0,0,0.06)] rounded-xl overflow-hidden" data-testid="travelers-section">
        <div className="flex items-center gap-2 px-4 py-3 bg-[#F9F8F4]">
          <Users className="w-4 h-4 text-[#C8A85A]" />
          <span className="text-sm font-medium text-[#1A1A1A] flex-1">Associated Travelers ({travelers.length})</span>
        </div>
        {travelers.length === 0 ? (
          <div className="p-4 text-center text-sm text-[#8C8C8C]">No travelers linked to this client</div>
        ) : (
          <div className="divide-y divide-[rgba(0,0,0,0.04)]">
            {travelers.map(t => (
              <button
                key={t.id}
                onClick={() => navigate(`/advisor/manage/travelers/${t.id}`)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#F9F8F4] transition-colors text-left"
                data-testid={`traveler-link-${t.id}`}
              >
                <div className="w-8 h-8 rounded-full bg-[#C8A85A]/10 flex items-center justify-center">
                  <User className="w-4 h-4 text-[#C8A85A]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1A1A1A]">{t.legal_full_name || t.full_name || t.preferred_name}</p>
                  <div className="flex items-center gap-2 text-xs text-[#8C8C8C]">
                    {t.email && <span>{t.email}</span>}
                    {t.passport_number && <span>Passport: ***{t.passport_number.slice(-4)}</span>}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-[#8C8C8C]" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* LINKED TRIPS */}
      <div className="border border-[rgba(0,0,0,0.06)] rounded-xl overflow-hidden" data-testid="trips-section">
        <div className="flex items-center gap-2 px-4 py-3 bg-[#F9F8F4]">
          <Plane className="w-4 h-4 text-[#C8A85A]" />
          <span className="text-sm font-medium text-[#1A1A1A] flex-1">Linked Trips ({trips.length})</span>
        </div>
        {trips.length === 0 ? (
          <div className="p-4 text-center text-sm text-[#8C8C8C]">No trips linked</div>
        ) : (
          <div className="divide-y divide-[rgba(0,0,0,0.04)]">
            {trips.map(t => (
              <button
                key={t.id}
                onClick={() => navigate(`/advisor/trips/${t.id}`)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#F9F8F4] transition-colors text-left"
                data-testid={`trip-link-${t.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1A1A1A]">{t.internal_trip_name || t.destinations || 'Trip'}</p>
                  <div className="flex items-center gap-2 text-xs text-[#8C8C8C]">
                    <span>{formatDate(t.trip_start_date || t.start_date)}</span>
                    {t.lifecycle_stage && <span className="px-1.5 py-0.5 rounded bg-[#C8A85A]/10 text-[#C8A85A]">{t.lifecycle_stage}</span>}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-[#8C8C8C]" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
